import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const AlertContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AlertProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [sessionAlertShown, setSessionAlertShown] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/alerts?acknowledged=false`);
      setPendingAlerts(response.data);
      
      // Check if alerts were already shown this session
      const sessionKey = `low_stock_alerts_shown_${user?.id}`;
      const alreadyShown = sessionStorage.getItem(sessionKey);
      
      // Check if there are alerts assigned to the current user
      const userAlerts = response.data.filter(
        alert => alert.assigned_user_id === user?.id || !alert.assigned_user_id
      );
      
      // Only show modal once per session
      if (userAlerts.length > 0 && !showAlertModal && !alreadyShown && !sessionAlertShown) {
        setCurrentAlert(userAlerts[0]);
        setShowAlertModal(true);
        sessionStorage.setItem(sessionKey, 'true');
        setSessionAlertShown(true);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, [token, user?.id, showAlertModal, sessionAlertShown]);

  useEffect(() => {
    if (token) {
      fetchAlerts();
      // No polling - alerts shown once per session
    }
  }, [token, fetchAlerts]);

  const acknowledgeAlert = async (alertId) => {
    if (!user) return;
    try {
      await axios.post(`${API}/alerts/${alertId}/acknowledge`, {
        user_id: user.id,
        user_name: user.name
      });
      setShowAlertModal(false);
      setCurrentAlert(null);
      fetchAlerts();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const assignAlert = async (alertId, userId, userName) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/assign`, {
        user_id: userId,
        user_name: userName
      });
      fetchAlerts();
    } catch (error) {
      console.error('Failed to assign alert:', error);
    }
  };

  const dismissCurrentAlert = () => {
    setShowAlertModal(false);
    // Show next alert if any
    const remaining = pendingAlerts.filter(a => a.id !== currentAlert?.id);
    if (remaining.length > 0) {
      setTimeout(() => {
        setCurrentAlert(remaining[0]);
        setShowAlertModal(true);
      }, 1000);
    } else {
      setCurrentAlert(null);
    }
  };

  return (
    <AlertContext.Provider value={{
      pendingAlerts,
      showAlertModal,
      currentAlert,
      setShowAlertModal,
      acknowledgeAlert,
      assignAlert,
      dismissCurrentAlert,
      fetchAlerts
    }}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};
